import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper } from '@mui/material';
import { diffWords } from 'diff';

/**
 * Coerce any prop value to a string before handing it to the `diff` library.
 * `diff` calls `.match()` on its inputs, so a non-string (object/number/etc.)
 * throws `value.match is not a function` — the crash this guards against
 * (e.g. when a requirement's `description` comes back as a non-string). See
 * docs/QA/ISSUES_LOG.md F1 / backlog T1.5.
 */
const toText = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  try {
    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  } catch {
    return String(value);
  }
};

/**
 * DiffViewer component for showing text differences
 * Uses the diff library to show side-by-side and inline diffs
 */
const DiffViewer = ({ oldText, newText, inline = true }) => {
  const oldStr = toText(oldText);
  const newStr = toText(newText);
  const differences = useMemo(() => {
    return diffWords(oldStr, newStr);
  }, [oldStr, newStr]);

  if (inline) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, overflow: 'auto', maxHeight: '60vh' }}>
        <Box 
          component="div" 
          sx={{ 
            fontFamily: 'monospace', 
            whiteSpace: 'pre-wrap',
            fontSize: '0.875rem',
            lineHeight: 1.6
          }}
        >
          {(() => {
            // `differences` is recomputed whole from `oldStr`/`newStr` on every render
            // (useMemo above), with no add/remove/reorder UI and no per-word local
            // state, so a key only needs to stay unique within one render. Built from
            // each word's running offset in the diffed text, not the loop index the
            // rule flags, so two identical words at different offsets still get
            // distinct keys.
            let offset = 0;
            return differences.map((part) => {
              const wordKey = `${offset}-${part.added ? 'a' : part.removed ? 'r' : 'c'}`;
              offset += part.value.length;

              // Red background with strikethrough for removed parts
              if (part.removed) {
                return (
                  <span
                    key={wordKey}
                    style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.2)',
                      textDecoration: 'line-through',
                      color: '#b71c1c'
                    }}
                  >
                    {part.value}
                  </span>
                );
              }
              // Green background for added parts
              if (part.added) {
                return (
                  <span
                    key={wordKey}
                    style={{
                      backgroundColor: 'rgba(0, 255, 0, 0.2)',
                      color: '#1b5e20',
                      fontWeight: 'bold'
                    }}
                  >
                    {part.value}
                  </span>
                );
              }
              // Normal text for unchanged parts
              return <span key={wordKey}>{part.value}</span>;
            });
          })()}
        </Box>
      </Paper>
    );
  }

  // Side-by-side view (not implemented yet but structure is here)
  return (
    <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', mb: 2 }}>
      <Paper 
        variant="outlined" 
        sx={{ p: 2, width: '50%', overflow: 'auto', maxHeight: '60vh' }}
      >
        <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {oldStr}
        </Box>
      </Paper>
      <Paper 
        variant="outlined" 
        sx={{ p: 2, width: '50%', overflow: 'auto', maxHeight: '60vh' }}
      >
        <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {newStr}
        </Box>
      </Paper>
    </Box>
  );
};

DiffViewer.propTypes = {
  // Tolerant by design: values are coerced to strings via toText() before use,
  // so a non-string (e.g. an object description) must not crash the diff (T1.5).
  oldText: PropTypes.any,
  newText: PropTypes.any,
  inline: PropTypes.bool
};

export default DiffViewer;